/**
 * Diccionario ESPAÑOL — idioma por defecto y FUENTE DE VERDAD del tipo
 * `Messages`. El resto de idiomas (`en.ts`, `de.ts`, `fr.ts`, `it.ts`) se
 * comprueban contra `typeof es` con `satisfies`, así que si a un idioma le
 * falta o le sobra una clave, `tsc` falla en ese fichero.
 *
 * Convención de nombres: prefijo por pantalla (`app*` = App.tsx, flujo de
 * mesas; `manage*` = ReservationManage.tsx; `confirm*` = ConfirmReservation.tsx;
 * `common*` = compartido entre las tres). Interpolación simple `{variable}`
 * vía `t(key, vars)`.
 *
 * NO van aquí: contenido que escribe el propio restaurante (avisos,
 * announcement, motivo del día, nombre de salones/turnos, notas del cliente,
 * motivo de cancelación) ni mensajes libres que ya manda el backend en
 * español (se muestran tal cual, ver `.claude/memorias/frontend.notas.md`).
 */

const es = {
  // ── Común (compartido por las 3 pantallas) ────────────────────────────
  commonPoweredBy: "Con la tecnología de",
  commonManageReservationLink: "Gestionar mi reserva",
  commonRestaurantConfirmAvailability:
    "El restaurante confirmará la disponibilidad.",
  commonConnectionErrorShort: "Error de conexión",
  commonConnectionErrorRetry: "Error de conexión. Inténtalo de nuevo.",
  commonConnectionErrorLater:
    "Error de conexión. Inténtalo de nuevo más tarde.",
  // Plurales simples (sin librería): se elige One/Other según n === 1.
  commonPeopleOne: "{n} persona",
  commonPeopleOther: "{n} personas",
  commonPeopleShortOne: "{n} pers.",
  commonPeopleShortOther: "{n} pers.",
  // Iniciales de los 7 días (L-D) para las cabeceras de los calendarios.
  commonCalendarWeekdays: ["L", "M", "X", "J", "V", "S", "D"],
  // Sufijo tras la hora ("20:00" + esto). Incluye el espacio si hace falta.
  commonTimeSuffix: " h",

  // Selector de menús por fecha, compartido entre el paso "menu" de App.tsx
  // y el editor de "Tu menú" de ReservationManage.tsx. Nombre/descripción del
  // menú son contenido del negocio y NUNCA se traducen (llegan tal cual del
  // backend).
  commonMenuShowDescription: "Ver descripción",
  commonMenuHideDescription: "Ocultar descripción",
  commonMenuAllGuestsLabel: "Para toda la mesa ({count} comensales)",
  commonMenuTotalLabel: "Seleccionado: {total} de {count} comensales",
  commonMenuExceedsPartySize:
    "El menú seleccionado supera el número de comensales.",
  commonMenuSummaryItem: "{qty}× {name}",

  // ── App.tsx (flujo de reserva de mesas) ───────────────────────────────
  appOrgNotFoundHeading: "Restaurante no encontrado",
  appOrgNotFoundFallback: "Restaurante no encontrado",
  appOrgLoadNetworkError: "Error al cargar los datos del restaurante",
  appOrgLoadGenericError: "No se pudo cargar la información del restaurante",

  appSuccessHeadingLargeGroup: "¡Solicitud Recibida!",
  appSuccessHeadingPending: "¡Solicitud enviada!",
  appSuccessHeadingConfirmed: "¡Reserva Confirmada!",
  appSuccessBodyLargeGroup:
    "Tu solicitud de grupo en {name} ha sido registrada. El restaurante se pondrá en contacto contigo.",
  appSuccessBodyPending:
    "Tu reserva en {name} está pendiente de aprobación. Te avisaremos en cuanto el restaurante la confirme.",
  appSuccessBodyConfirmed: "Tu reserva en {name} ha sido registrada.",
  appSuccessEmailNotePending: "Recibirás el aviso de confirmación en {email}",
  appSuccessEmailNoteConfirmed:
    "Recibirás un email de confirmación en {email}",
  appNewReservationButton: "Hacer otra reserva",

  appPartySizeLabel: "Número de personas",
  appLargeGroupButton: "Grupo grande (+{threshold} personas)",
  appLargeGroupQuestion: "¿Cuántas personas sois?",
  appLargeGroupPlaceholder: "Ej.: 25",

  appSelectDayLabel: "Selecciona día",
  appShowMoreDates: "Ver más fechas",

  appSelectRoomLabel: "Selecciona sala",
  appNoRoomsAvailable: "No hay salones disponibles para este día.",

  appSelectTimeLabel: "Selecciona hora",
  appDefaultClosedReason: "Día de cierre",
  appNoSlotsAvailable: "No hay horarios disponibles",
  appDefaultShiftLunch: "COMIDA",
  appDefaultShiftDinner: "CENA",
  appNoTablesAvailable: "No hay mesas disponibles",
  appContinueButton: "Continuar",

  // Paso "menu" (opcional, solo si el día elegido tiene menús).
  appMenuQuestionHeading: "¿Queréis menú?",
  appMenuNoThanksButton: "No, gracias",
  appMenuYesButton: "Sí",
  appMenuListHeading: "Elige tu menú",
  appMenuSummaryHeading: "Tu menú:",

  appSummaryModify: "Modificar",
  appPhoneLabel: "Teléfono *",
  appPhonePlaceholder: "612 345 678",
  appNameLabel: "Nombre *",
  appNamePlaceholder: "Tu nombre",
  appLastNameLabel: "Apellidos *",
  appLastNamePlaceholder: "Tus apellidos",
  appEmailLabel: "Email *",
  appEmailPlaceholder: "tu@email.com",
  appEmailInvalid: "Por favor, introduce un email válido",
  appNotesLabel: "Comentarios (opcional)",
  appNotesPlaceholderDefault:
    "Reservar platos, es un cumpleaños, sillita de bebé, movilidad reducida, etc.",

  appAcceptTermsPrefix: "Acepto los",
  appTermsLinkLabel: "Términos y Condiciones",
  appAcceptPrivacyPrefix: "Acepto la",
  appPrivacyLinkLabel: "Política de Privacidad",
  appSpendNotice:
    "{restaurant} guarda en tu ficha de cliente el importe de la cuenta de tu reserva. Puedes oponerte en cualquier momento. Más información:",
  appMarketingCheckbox: "Recibir ofertas y novedades del restaurante",

  appLargeGroupSummary: "Grupo grande ({people})",

  appBackButton: "Atrás",
  appSubmitButton: "Reservar",
  appSubmitting: "Enviando...",
  appSubmitErrorFallback: "Error al crear la reserva",

  appFullCalendarTitle: "Selecciona fecha",

  // ── ReservationManage.tsx (gestionar reserva desde el enlace) ────────
  manageLoading: "Cargando tu reserva...",
  manageNotFoundHeading: "Reserva no encontrada",
  manageFetchErrorFallback: "Reserva no encontrada",
  manageConfirmErrorFallback: "Error al confirmar",
  manageCancelErrorFallback: "Error al cancelar",
  manageModifyErrorFallback: "Error al modificar la reserva",

  statusPending: "Pendiente de confirmación",
  statusConfirmed: "Confirmada",
  statusCancelled: "Cancelada",
  statusCompleted: "Completada",
  statusNoShow: "No Show",
  statusCancelledByEstablishment: "Cancelada por el restaurante",

  manageGoogleEventTitle: "Reserva en {name}",
  manageGoogleDetailsIntro: "Reserva para {people}",
  manageGoogleDetailsCode: "Código: {code}",
  manageGoogleDetailsNotes: "Notas: {notes}",

  manageConfirmSuccessHeading: "¡Reserva confirmada!",
  manageConfirmSuccessBody: "Te esperamos el {date} a las {time}",
  manageAddToGoogleCalendar: "Añadir a Google Calendar",

  manageCancelSuccessHeading: "Reserva cancelada",
  manageCancelSuccessBodyLine1: "Tu reserva ha sido cancelada correctamente.",
  manageCancelSuccessBodyLine2: "Esperamos verte pronto.",

  manageModifySuccessHeading: "Reserva modificada",
  manageModifySuccessBodyLine1: "Tu solicitud de cambio ha sido enviada.",
  manageModifySuccessBodyLine2: "Recibirás confirmación pronto.",

  manageCancelledByRestaurantTitle: "El restaurante ha cancelado esta reserva",
  manageCancelledByRestaurantBody:
    "Lamentamos las molestias. Te invitamos a reservar otro día o a contactar directamente con el restaurante.",
  manageCancellationReasonLabel: "Motivo indicado por el restaurante",

  manageYourReservationHeading: "Tu reserva",
  manageRoomLabel: "Salón: {roomName}",
  manageCodeLabelBlock: "Código de reserva",
  manageNotesLabel: "Notas",

  // Sección "Tu menú" (menús por fechas) + modal "Cambiar menú".
  manageMenuHeading: "Tu menú",
  manageMenuChangeButton: "Cambiar",
  manageMenuNoneSelected: "No has añadido ningún menú a tu reserva.",
  manageMenuNotEditableNotice:
    "La elección de menú se puede cambiar hasta el día anterior a la reserva.",
  manageMenuModalTitle: "Cambiar menú",
  manageMenuSaveErrorFallback: "Error al guardar el menú",

  managePendingApprovalBannerTitle: "Esperando aprobación del restaurante.",
  managePendingApprovalBannerBody:
    "Cuando la confirmen recibirás un aviso y podrás ratificar tu asistencia desde aquí.",
  manageConfirmDisabledTitle:
    "Disponible cuando el restaurante apruebe tu solicitud",
  manageConfirmDisabledLabel: "Confirmar asistencia (pendiente de aprobación)",
  manageConfirmButton: "Confirmar asistencia",

  managePendingChangeBannerTitle: "Ya tienes una solicitud de cambio pendiente.",
  managePendingChangeBannerFooter:
    "El restaurante la revisará y te confirmará en breve.",
  managePendingChangeDisabledTitle:
    "Ya tienes una solicitud de cambio pendiente de revisión",
  managePendingChangeDisabledLabel: "Solicitud de cambio pendiente",
  manageModifyButton: "Modificar reserva",
  manageCancelButton: "Cancelar reserva",

  managePendingSummaryDate: "fecha: {date}",
  managePendingSummaryTime: "hora: {time}",
  managePendingSummaryPeople: "personas: {n}",

  manageWhatsAppButton: "Contactar por WhatsApp",

  manageModalConfirmTitle: "Confirmar asistencia",
  manageModalConfirmQuestion:
    "¿Confirmas que asistirás a tu reserva en {name}?",
  manageModalConfirmBack: "Volver",
  manageModalConfirmSubmit: "Confirmar",

  manageModalCancelTitle: "Cancelar reserva",
  manageModalCancelWarningTitle: "¿Estás seguro?",
  manageModalCancelWarningBody:
    "Esta acción no se puede deshacer. Si cancelas, perderás tu reserva en {name}.",
  manageModalCancelKeep: "Mantener",
  manageModalCancelSubmit: "Cancelar",

  manageModalEditTitle: "Modificar reserva",
  manageModalEditDateLabel: "Fecha",
  manageModalEditTimeLabel: "Hora",
  manageModalEditPartyLabel: "Número de personas",
  manageModalEditSummaryPrefix: "Nueva reserva:",
  manageModalEditSummaryAt: "a las",
  manageModalEditSummaryFor: "para",
  manageModalEditCancel: "Cancelar",
  manageModalEditSubmit: "Guardar cambios",

  // ── ConfirmReservation.tsx (confirmar desde el email/WhatsApp) ───────
  confirmLoading: "Confirmando tu reserva...",
  confirmErrorHeading: "No se pudo confirmar",
  confirmAlreadyCancelled: "Esta reserva ya ha sido cancelada.",
  confirmGenericErrorFallback: "Error al confirmar la reserva",

  confirmAwaitingHeading: "Pendiente de aprobación",
  confirmAwaitingBody:
    "Tu reserva está pendiente de aprobación por el restaurante. Cuando la confirmen recibirás un aviso por email o WhatsApp y podrás reconfirmarla desde aquí.",
  confirmCodeLabelInline: "Código de reserva:",

  confirmAlreadyHeading: "Reserva ya confirmada",
  confirmSuccessHeading: "¡Reserva confirmada!",
  confirmAlreadyBody: "Tu reserva ya estaba confirmada anteriormente.",
  confirmSuccessBody: "Gracias por confirmar tu asistencia.",
  confirmFooterText: "Te esperamos. ¡Buen provecho!",

  // ── Clases (ClassesApp.tsx, widget del módulo Clases) ─────────────────
  classesPickDay: "Elige el día",
  classesWeekPrev: "Semana anterior",
  classesWeekNext: "Semana siguiente",
  classesCountOne: "{n} clase",
  classesCountOther: "{n} clases",
  classesNoClassesThisDay: "No hay clases este día",
  classesSeatsLeft: "Quedan {n} de {total}",
  classesFull: "Completo",
  classesMinutes: "{n} min",
  classesTeacher: "Prof. {name}",
  classesPeople: "Personas",
  classesName: "Nombre y apellidos",
  classesEmail: "Email",
  classesPhone: "Teléfono (opcional)",
  classesMarketing: "Quiero recibir novedades de este negocio",
  classesSubmit: "Confirmar reserva",
  classesSubmitting: "Reservando…",
  classesFieldsError: "Revisa el nombre y el email",
  classesOnlyLeft: "Solo quedan {n} plazas libres en esta clase",
  classesFullNow: "La clase se acaba de llenar. Elige otra hora u otro día.",
  classesSuccessTitle: "¡Reserva confirmada!",
  classesSuccessCode: "Código de reserva",
  classesSuccessEmailSent:
    "Te hemos enviado un email con los detalles y el enlace para gestionar tu reserva.",
  classesNewBooking: "Hacer otra reserva",
  classesLoadError: "No se pudo cargar la información. Inténtalo de nuevo.",
  classesCompanyToggle: "Reservo de parte de una empresa (hotel, agencia…)",
  classesCompanyName: "Nombre de la empresa u hotel",
};

export default es;
export type Messages = typeof es;
